import React, { CSSProperties, useState } from "react";
import { useDelayHover } from "../helper/hook-helper";
import { classArr } from "../helper/style-helper";
import BottomTooltip, { BottomTooltipEdge } from "./bottom-tooltip";
import * as styles from "./hover-tooltip.module.scss";
import TooltipPortal from "./tooltip-portal";

export interface HoverTooltipProps {
	children: React.ReactNode;
	/** Tooltip contents. When omitted/null/false, no tooltip is shown. */
	tooltip?: React.ReactNode;
	edge?: BottomTooltipEdge;
	/** Class applied to the tooltip content container */
	className?: string;
	/** Class applied to the hover wrapper */
	wrapperClassName?: string;
	style?: CSSProperties;
	/** Delay in ms before showing the tooltip. Defaults to useDelayHover's default. */
	delay?: number;
	/**
	 * When true, positions the tooltip with fixed coordinates from the wrapper
	 * (avoids clipping inside overflow:hidden ancestors).
	 */
	fixed?: boolean;
}

/**
 * Wraps a child and shows a BottomTooltip on hover.
 * Hover state is delayed via useDelayHover so tooltips don't flash on quick mouseovers.
 */
export default function HoverTooltip(props: HoverTooltipProps) {
	const [isHover, enableHover, disableHover] = useDelayHover(props.delay);
	const [wrapperRef, setWrapperRef] = useState<HTMLSpanElement | null>(null);
	const hasTooltip =
		props.tooltip !== undefined &&
		props.tooltip !== null &&
		props.tooltip !== false &&
		props.tooltip !== "";

	const tooltip = (
		<BottomTooltip
			edge={props.edge}
			className={props.className}
			refPosition={props.fixed ? wrapperRef : undefined}
		>
			{props.tooltip}
		</BottomTooltip>
	);

	return (
		<span
			ref={setWrapperRef}
			style={props.style}
			className={classArr(styles.wrapper, props.wrapperClassName)}
			onMouseEnter={hasTooltip ? enableHover : undefined}
			onMouseLeave={hasTooltip ? disableHover : undefined}
		>
			{props.children}
			{isHover &&
				hasTooltip &&
				(props.fixed ? (
					<TooltipPortal>{tooltip}</TooltipPortal>
				) : (
					tooltip
				))}
		</span>
	);
}
